import styled from 'styled-components';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import { Button, Grid, Icon } from 'semantic-ui-react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import { GuarantorsConsumerRenderProps, withGuarantorsContext } from 'components/guarantors/GuarantorsContext';
import { NotificationSystemProps, withNotificationSystemContext } from 'context/NotificationSysemContext';
import {
  GuarantorDto,
  MedIndexAddRequest,
  MedIndexAddSearchRequest,
  MedIndexAddSearchResponse
} from 'ts-types/api.types';
import axios, { CancelTokenSource } from 'axios';
import React, { Component } from 'react';
import { isKeyCheck } from 'utils/keyUtils';
import { noop } from 'utils/functionUtils';
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import FormContainer from 'components/layout/FormContainer';
import FormContent from 'components/layout/FormContent';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import Input from 'components/final-form/Input';
import SearchByButtonsStyle from 'components/layout/SearchByButtonsStyle';
import LoaderComponent from 'components/LoaderComponent';
import { CheckboxSc } from 'components/final-form/CheckBox';
import { medIndexAddGuarantors, medIndexAddSearchGuarantors } from 'services/guarantorServices';

const StyledTable = styled(VirtualizedTable)`
  .ReactVirtualized__Table__Grid {
    outline: none;
    border: 1px solid rgba(34, 36, 38, .15);
    border-top: none;
    border-radius: 0 0 .28571429rem .28571429rem;
  }

  .ReactVirtualized__Table__row {
    border-left: none;
  }

  .ReactVirtualized__Table__row.totalRow {
    font-weight: 700;
    background-color: rgba(0, 0, 0, .2);

    &:hover {
      background-color: rgba(0, 0, 0, .2) !important;
    }
  }

  .ReactVirtualized__Table__headerColumn {
    height: 100%;
    border-right: 1px solid rgba(34, 36, 38, .15);
    padding-top: 0.85rem;
  }

  .ReactVirtualized__Table__headerColumn:last-child {
    border-right: none;
  }

  .ReactVirtualized__Table__rowColumn {
    height: 100%;
    border-right: 1px solid rgba(34, 36, 38, .15);
    padding-top: 0.85rem;
  }

  .ReactVirtualized__Table__rowColumn.noBorder {
    border-right: none;
  }

  .ReactVirtualized__Table__rowColumn:last-child {
    border-right: none;
  }
`;

const StyledGrid = styled(Grid)`
  flex: 1 0 200px;
  margin-top: 1rem !important;
`;

const StyledMedicamentsTableContainer = styled.div`
  height: 500px;
`;

const ButtonsDivider = styled.div`
  margin-top: 2rem;
`;

const StyledCell = styled.div`
  text-align: right;
  padding-right: 0.4rem;
`;

interface Props extends MessageSourceProps, AuthConsumerRenderProps, GuarantorsConsumerRenderProps, NotificationSystemProps {
  onClose: (importing?: boolean) => void
}

interface State {

  foundGuarantors: Array<GuarantorDto>,
  guarantorsLoaded: boolean,
  searchBy: string,
  cancelTokenSource: CancelTokenSource,
  errorMessage?: string
}

class MedIndexAddView extends Component<Props, State> {

  private mounted: boolean = false;

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      foundGuarantors: [],
      guarantorsLoaded: true,
      searchBy: "description",
      cancelTokenSource
    };
  }

  componentDidMount(): void {
    this.mounted = true;
  }

  componentWillUnmount(): void {
    this.mounted = false;

    const {cancelTokenSource} = this.state;
    cancelTokenSource && cancelTokenSource.cancel('Operation canceled by user');
  }

  searchByCriteria = (criteria: string) => {
    this.setState({
      searchBy: criteria
    });
  };

  initialFormValues = () => {
    const {currentUser} = this.props;
    return {
      mandateId: currentUser!.userConfig.mandateId
    }
  }

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  }

  handleError = () => {
    const {getMessage} = this.props;
    this.setErrorMessage(getMessage("error.general"));
  }

  triggerSearchOnEnter(values: any, event: any, source?: string) {
    event.persist();

    const isKey: any = isKeyCheck(event);

    if (isKey.enter) {
      // this.handleSearch(values);
    }
  }

  handleSubmit = (values: any) => {

    const {selectionNotificationSystem, onClose, getMessage} = this.props;
    const {foundGuarantors, cancelTokenSource} = this.state;

    const selectedGuarantors = foundGuarantors.filter(g => g.selection);
    const eanNumber: Array<string> = selectedGuarantors.map(g => g.eanNumber);

    const request: MedIndexAddRequest = {
      mandateId: values.mandateId,
      eanNumber: eanNumber
    }

    const onFinally = () => {
      onClose(true);
    }

    medIndexAddGuarantors(request, cancelTokenSource)
        .then(response => {
          // @ts-ignore
          const notification = selectionNotificationSystem.current;
          if (notification) {
            notification.addNotification({
              title: getMessage("guarantor.update.success"),
              message: getMessage("guarantorUpdate.successfulUpdate"),
              level: 'success'
            });

            const guarantors = response.guarantors;
            this.setState({
              foundGuarantors: guarantors
            });
          }
        })
        .catch(this.handleError)
        .finally(onFinally);
  };

  handleSearch = (values: any): void => {
    if (!this.mounted) {
      return;
    }

    const {currentUser} = this.props;
    const {searchBy, cancelTokenSource} = this.state;

    this.setState({
      guarantorsLoaded: false
    });

    const onFinally = () => {
      this.setState({
        guarantorsLoaded: true
      })
    };

    const request: Partial<MedIndexAddSearchRequest> = {
      mandateId: currentUser!.userConfig.mandateId,
      description: searchBy === "description" && values.description ? values.description : undefined,
      eanNumber: searchBy === "eanNumber" && values.eanNumber ? values.eanNumber : undefined
    }

    medIndexAddSearchGuarantors(request, cancelTokenSource)
        .then((response: MedIndexAddSearchResponse) => {
          let foundGuarantors = response.guarantors;

          this.setState({
            foundGuarantors: foundGuarantors
          });
        })
        .catch(noop)
        .finally(onFinally);
  }

  render(): React.ReactNode {
    return (
        <React.Fragment>
          {this.renderSearchGuarantorSegment()}
        </React.Fragment>
    );
  }

  renderSearchGuarantorSegment = (): JSX.Element => {
    return (
        <FinalForm
            onSubmit={() => {
            }}
            initialValues={this.initialFormValues()}
            subscription={{pristine: true, values: true}}
            render={this.renderSearchForm}
        />
    );
  };

  renderSearchForm = ({values}: FormRenderProps): React.ReactNode => {

    const {onClose, getMessage} = this.props;
    const {foundGuarantors, guarantorsLoaded, errorMessage, searchBy} = this.state;

    let disableSearch = true;

    const searchDescription = searchBy === "description" && values.description && values.description.length > 0;
    const searchEanNumber = searchBy === "eanNumber" && values.eanNumber && values.eanNumber.length > 0;

    if (searchDescription || searchEanNumber) {
      disableSearch = false;
    }

    const countSelectedGuarantors = foundGuarantors.filter(g => g.selection).length;

    return (
        <FormContainer>
          <FormContent>
            <PraxFormGrid>
              {errorMessage
              && (
                  <Grid.Row>
                    <Grid.Column>
                      <StyledErrorMessage onDismiss={() => this.setErrorMessage()}>
                        {errorMessage}
                      </StyledErrorMessage>
                    </Grid.Column>
                  </Grid.Row>
              )
              }
              <Grid.Row>
                <Grid.Column width={4} verticalAlign="middle">
                  {getMessage(`${searchBy}`)}:
                </Grid.Column>
                <Grid.Column width={searchBy === "eanNumber" ? 4 : 9}>
                  <Field
                      name={searchBy}
                      component={Input}
                      fluid
                      autoFocus
                      onKeyDown={(e: any) => this.triggerSearchOnEnter(values, e, 'search')}
                  />
                </Grid.Column>
                <Grid.Column width={searchBy === "eanNumber" ? 8 : 3} textAlign="right">
                  <Button type="button" primary compact
                          onClick={() => this.handleSearch(values)}
                          disabled={disableSearch}
                  >
                    {getMessage("search")}
                  </Button>
                </Grid.Column>
              </Grid.Row>
              <Grid.Row>
                <Grid.Column width={4} verticalAlign="middle">
                  {getMessage("guarantor.search.by")}:
                </Grid.Column>
                <Grid.Column width={6}>
                  <SearchByButtonsStyle compact size="tiny">
                    <Button primary={searchBy === 'eanNumber' && true}
                            basic={searchBy === 'eanNumber' ? undefined : true}
                            onClick={() => this.searchByCriteria("eanNumber")}
                    >
                      {getMessage("eanNumber")}
                    </Button>
                    <Button primary={searchBy === 'description' && true}
                            basic={searchBy === 'description' ? undefined : true}
                            onClick={() => this.searchByCriteria("description")}
                    >
                      {getMessage("description")}
                    </Button>
                  </SearchByButtonsStyle>
                </Grid.Column>
              </Grid.Row>
            </PraxFormGrid>
          </FormContent>
          <StyledGrid>
            <Grid.Row>
              <Grid.Column width={16}>
                <StyledMedicamentsTableContainer>
                  {!guarantorsLoaded ?
                      <LoaderComponent message={getMessage("guarantor.loading")}/> :
                      this.renderGuarantorSegment()}
                </StyledMedicamentsTableContainer>
              </Grid.Column>
            </Grid.Row>
          </StyledGrid>
          <ButtonsDivider>
            <Button compact type="submit"
                    primary
                    onClick={() => this.handleSubmit(values)}
                    disabled={!countSelectedGuarantors}
            >
              {getMessage("import")}
            </Button>
            <Button compact type="button" secondary onClick={() => onClose(false)}>
              {getMessage("button.close")}
            </Button>
          </ButtonsDivider>
        </FormContainer>
    )
  }

  selectGuarantor = (index: number) => {
    const {foundGuarantors} = this.state;

    let editGuarantors = [...foundGuarantors];
    let guarantor = editGuarantors[index];

    if (guarantor) {
      guarantor.selection = !guarantor.selection;
      this.setState({
        foundGuarantors: editGuarantors
      })
    }
  };

  foundGuarantorRowGetter = ({index}: any) => {
    const {foundGuarantors} = this.state;
    return foundGuarantors[index];
  }

  foundGuarantorRowRenderer = ({className, columns, index, key, style}: any) => {
    const a11yProps = {'aria-rowindex': index + 1};

    const {foundGuarantors} = this.state;

    if (!foundGuarantors[index].selection) {
      style.color = '#c6c6cb';
    }

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={style}
            onDoubleClick={() => this.selectGuarantor(index)}
        >
          {columns}
        </div>
    );
  }

  guarantorSelectCellRenderer = (data: any) => {

    const {rowIndex, cellData} = data;

    return (
        <CheckboxSc
            checked={cellData}
            onChange={() => this.selectGuarantor(rowIndex)}
        />
    )
  };

  notificationCellRenderer = ({cellData}: any) => {
    if (cellData) {
      return <Icon name="check" size="small"/>
    }
    return ""
  }

  tariffTypeCellRenderer = ({cellData}: any) => {

    const {getMessage} = this.props;

    if (cellData) {

      return getMessage(`tariffType.${cellData.toLowerCase()}`)
    }
  }

  renderGuarantorSegment = (): JSX.Element => {

    const {getMessage} = this.props;
    const {foundGuarantors} = this.state;
    return (
        <StyledTable
            rowCount={foundGuarantors.length}
            rowGetter={this.foundGuarantorRowGetter}
            rowRenderer={this.foundGuarantorRowRenderer}
            columns={[
              {
                width: 100,
                label: (getMessage("select")),
                dataKey: "selection",
                cellRenderer: this.guarantorSelectCellRenderer
              },
              {
                width: 200,
                label: (getMessage("eanNumber")),
                dataKey: "eanNumber"
              },
              {
                width: 200,
                label: (getMessage("bagNumber")),
                dataKey: "bagNumber"
              },
              {
                width: 200,
                label: (getMessage("tariff")),
                dataKey: "tariffType",
                cellRenderer: (this.tariffTypeCellRenderer)
              },
              {
                width: 400,
                flexGrow: 1,
                label: (getMessage("description")),
                dataKey: "description"
              },
              {
                width: 200,
                label: (getMessage("city")),
                dataKey: "city"
              },
              {
                width: 200,
                label: (getMessage("street")),
                dataKey: "street"
              },
              {
                width: 100,
                label: (getMessage("active")),
                dataKey: "active",
                cellRenderer: this.notificationCellRenderer
              }
            ]}
        />
    );
  };

}

const MedIndexAddViewWrapper =
    withMessages(
        withAuthContext(
            withGuarantorsContext(
                withNotificationSystemContext(
                    MedIndexAddView))));

export default MedIndexAddViewWrapper;
